import { Form } from '../../components/ui/validated-form';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { adminAPI, authAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Shield, Eye, EyeOff } from 'lucide-react';

const ADMIN_DIALOG_FOOTER_CLASS = 'sticky bottom-[-1rem] sm:bottom-[-1.5rem] -mx-4 sm:-mx-6 mt-auto flex flex-col-reverse gap-2 border-t border-[#E5E5EA] bg-background px-4 py-3 sm:flex-row sm:justify-end sm:space-x-2 sm:px-6';

export default function AdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [isAdmin, setIsAdmin] = useState(null);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const { login, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = searchParams.get('next') || '/admin';

  const isAbortLike = (err) =>
    err.name === 'AbortError' ||
    err.name === 'CanceledError' ||
    err.code === 'ERR_CANCELED' ||
    String(err.message || '').toLowerCase().includes('aborted');

  useEffect(() => {
    if (!isAuthenticated) {
      setChecking(false);
      setIsAdmin(null);
      return;
    }
    const controller = new AbortController();
    setChecking(true);
    adminAPI
      .me({ signal: controller.signal })
      .then(() => {
        setIsAdmin(true);
        navigate(next, { replace: true });
      })
      .catch((err) => {
        if (isAbortLike(err)) return;
        setIsAdmin(false);
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setChecking(false);
      });
    return () => controller.abort();
  }, [isAuthenticated, next, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      try {
        await adminAPI.me();
        toast.success('Admin angemeldet');
        navigate(next, { replace: true });
      } catch {
        await logout();
        toast.error('Kein Admin-Zugriff für diesen Account');
      }
    } catch (error) {
      toast.error(error.message || 'Anmeldung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F0F10] text-white flex items-center justify-center p-6">
      <div className="w-full max-w-md space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
            <Shield className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold">Admin Login</h1>
            <p className="text-sm text-white/60">Super Admin Zugriff</p>
            {checking && (
              <p className="text-xs text-white/40 mt-1">Admin-Zugriff wird geprüft...</p>
            )}
          </div>
        </div>

        {isAuthenticated && isAdmin === false && (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-4 text-sm">
            <p>Du bist eingeloggt, aber kein Super Admin.</p>
            <Button className="btn-primary mt-3" onClick={logout}>
              Abmelden
            </Button>
          </div>
        )}

        <Form onSubmit={handleSubmit} className="bg-white/5 border border-white/10 rounded-2xl p-6 space-y-5">
          <div className="space-y-2">
            <Label htmlFor="admin-email" className="text-white/80">E-Mail</Label>
            <Input
              id="admin-email"
              type="email"
              autoComplete="email"
              placeholder="admin@beispiel.de"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-base bg-white/10 text-white placeholder:text-white/40 border-white/10"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="admin-password" className="text-white/80">Passwort</Label>
            <div className="relative">
              <Input
                id="admin-password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-base pr-10 bg-white/10 text-white placeholder:text-white/40 border-white/10"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => {
                  setForgotEmail(email);
                  setForgotOpen(true);
                }}
                className="text-sm text-white/70 hover:text-white underline"
              >
                Passwort vergessen?
              </button>
            </div>
          </div>

          <Button type="submit" className="btn-primary w-full" disabled={loading}>
            {loading ? 'Wird angemeldet...' : 'Admin anmelden'}
          </Button>
        </Form>

        <div className="text-sm text-white/60">
          <Link to="/" className="hover:text-white underline">
            Zur Startseite
          </Link>
        </div>

        <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
          <DialogContent sending={forgotLoading} sendingLabel="E-Mail" className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Admin Passwort vergessen</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-[#86868B] mb-4">
              Gib deine Admin-E-Mail-Adresse ein. Wir senden einen Link zum Zurücksetzen des Passworts.
            </p>
            <Form
              onSubmit={async (e) => {
                e.preventDefault();
                const em = (forgotEmail || '').trim();
                if (!em) {
                  toast.error('Bitte E-Mail-Adresse eingeben');
                  return;
                }
                setForgotLoading(true);
                try {
                  await authAPI.forgotPassword({ email: em });
                  toast.success('Falls ein Admin-Konto existiert, wurde ein Link gesendet.');
                  setForgotOpen(false);
                  setForgotEmail('');
                } catch (err) {
                  toast.error(err?.response?.data?.detail || 'Fehler beim Senden');
                } finally {
                  setForgotLoading(false);
                }
              }}
              className="space-y-4"
            >
              <div className="space-y-2">
                <Label htmlFor="admin-forgot-email">E-Mail</Label>
                <Input
                  id="admin-forgot-email"
                  type="email"
                  placeholder="admin@beispiel.de"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  className="input-base"
                  required
                />
              </div>
              <DialogFooter className={ADMIN_DIALOG_FOOTER_CLASS}>
                <Button type="submit" className="btn-primary w-full sm:w-auto" disabled={forgotLoading}>
                  {forgotLoading ? 'Wird gesendet...' : 'Link senden'}
                </Button>
              </DialogFooter>
            </Form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
